import { Button } from '@hover/blueprint';
import React from 'react';
import { Redirect } from 'react-router';

import { useProfessionalSignUp } from 'src/modules/app/hooks/useProfessionalSignUp';
import type { PartialProfileFormData } from 'src/modules/signUp/components/Professional/types';
import { Alert } from 'src/shared/components';
import { Form as SharedForm } from 'src/shared/components/forms';
import { useCompleteUserProfileMutation } from 'src/shared/graphql/generated';
import { useLocation, useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { Route } from 'src/shared/route';
import { baseUrlToRedirectUrl } from 'src/shared/utils/url';

import { Fields } from '../Fields';

import { makeValidationSchema } from './validationSchema';

const PartialProfileForm = () => {
  const t = useTranslate(TranslationNamespace.SIGN_UP);
  const { user, userDispatch } = useProfessionalSignUp();
  const { search } = useLocation();

  const [completeUserProfile, { error, loading }] =
    useCompleteUserProfileMutation();

  if (!('needsUserUpdate' in user)) {
    return <Redirect to={Route.SIGN_UP_PROFESSIONAL.path + search} />;
  }

  const initialValues = {
    company: user.company,
    familyName: user.familyName,
    givenName: user.givenName,
    mobilePhone: 'mobilePhone' in user ? user.mobilePhone : '',
  };

  const validationSchema = makeValidationSchema(t);

  const hasErrorAtIdle = error && !loading;

  const onSubmit = async (formData: PartialProfileFormData) => {
    const orgData = { mobilePhone: formData.mobilePhone };

    userDispatch({
      type: 'UPDATE_PARTIAL_PROFILE',
      payload: orgData,
    });

    try {
      await completeUserProfile({ variables: orgData });

      window.location.href = baseUrlToRedirectUrl(window.location.href);
    } catch {
      // TODO: expected authentication errors should probably be
      // handled in an Apollo error link at the client level
    }
  };

  return (
    <SharedForm<PartialProfileFormData>
      initialValues={initialValues}
      onSubmit={onSubmit}
      testId="org-form"
      validationSchema={validationSchema}
    >
      <Fields needsUserUpdate />
      {hasErrorAtIdle && (
        <Alert alignSelf="stretch" flexDirection="row">
          {t('somethingWentWrongTryAgain')}
        </Alert>
      )}
      <Button
        alignSelf="stretch"
        isLoading={loading}
        marginTop={500}
        size="large"
        testId="create-account-button"
        type="submit"
      >
        {t('finish')}
      </Button>
    </SharedForm>
  );
};

export { PartialProfileForm };
